import React from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  Photo as PhotoIcon,
  Close as CloseIcon
} from '@mui/icons-material';

interface CoverImageUploadProps {
  coverPhotoUrl: string;
  onImageChange: (url: string) => void;
  onImageUpload: (file: File) => Promise<void>;
  title?: string;
  height?: number;
  loading?: boolean;
}

const CoverImageUpload: React.FC<CoverImageUploadProps> = ({
  coverPhotoUrl,
  onImageChange,
  onImageUpload,
  title = 'Cover Image',
  height = 200,
  loading = false
}) => {
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      await onImageUpload(file);
    } catch (error) {
      console.error('Error uploading image:', error);
    }
  };

  return (
    <Box sx={{ mt: 2, mb: 1 }}>
      <Typography variant="subtitle2" gutterBottom>
        {title}
      </Typography>
      {coverPhotoUrl ? (
        <Box sx={{ 
          position: 'relative', 
          mb: 2,
          borderRadius: 1,
          overflow: 'hidden',
          boxShadow: 1
        }}>
          <img 
            src={coverPhotoUrl} 
            alt="Cover preview" 
            style={{ 
              width: '100%', 
              height: height, 
              objectFit: 'cover',
              display: 'block'
            }}
          />
          {loading ? (
            <Box 
              sx={{ 
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0,0,0,0.3)'
              }}
            >
              <CircularProgress sx={{ color: 'white' }} />
            </Box>
          ) : (
            <IconButton 
              sx={{ 
                position: 'absolute', 
                top: 8, 
                right: 8, 
                bgcolor: 'rgba(0,0,0,0.5)',
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.7)'
                }
              }}
              onClick={() => onImageChange('')}
            >
              <CloseIcon sx={{ color: 'white' }} />
            </IconButton>
          )}
        </Box>
      ) : (
        <Button
          variant="outlined"
          component="label"
          startIcon={loading ? <CircularProgress size={20} /> : <PhotoIcon />}
          fullWidth
          disabled={loading}
          sx={{ 
            height: 100,
            borderStyle: 'dashed',
            '&:hover': {
              borderStyle: 'dashed'
            }
          }}
        >
          {loading ? 'Uploading...' : 'Upload Cover Image'}
          <input
            type="file"
            hidden
            accept="image/*"
            onChange={handleFileChange}
            disabled={loading}
          />
        </Button>
      )}
    </Box>
  );
};

export default CoverImageUpload; 